
















































































































import Vue from 'vue';
import { Component, Emit, Inject, Prop, Watch } from 'vue-property-decorator';
import { Location, Route } from 'vue-router';
import { namespace } from 'vuex-class';

const FreemiumFilterBadge = () =>
	import(/* webpackChunkName: "freemium-filter-badge" */ '@/components/filter-bar/FreemiumFilterBadge.vue');

import LoginMenuButton from '@/components/nav/LoginMenuButton.vue';
import LoginMenuButtonNew from '@/components/nav/LoginMenuButtonNew.vue';
import SettingsButton from '@/components/nav/static-pages/SettingsButton.vue';
import SearchSuggesterBar from '@/components/search/SearchSuggesterBar.vue';

import { CollectionType } from '@/enums/collection-type';
import { OnboardingStatus } from '@/enums/onboarding-status';
import { isSportSupported } from '@/enums/sports';
import { WebLocale } from '@/enums/web-locale';

import Genre from '@/interfaces/genre';
import Provider from '@/interfaces/provider';
import { HtmlContentMovieDetails, UrlMetadataResponse } from '@/interfaces/responses/url-metadata';

import { providerPageExpTrackEvent } from '@/helpers/experiments/popular-provider-page-exp-helper';
import StateHelper from '@/helpers/state-helper';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import type { FilterCollection } from '@/stores/filter.store';
import { isEditorialLocale } from '@/routing/editorial';

const Constant = namespace('constant');
const Filter = namespace('filter');
const Language = namespace('language');
const Routing = namespace('routing');
const SearchSuggester = namespace('searchSuggester');
const User = namespace('user');

@Component({
	name: 'AppWebHeader',
	components: {
		FreemiumFilterBadge,
		LoginMenuButton,
		LoginMenuButtonNew,
		SearchSuggesterBar,
		SettingsButton,
	},
})
export default class AppWebHeader extends Vue {
	currentWatchlistRoute: Location | null = null;

	@Prop({ required: true }) searchSuggesterIsShown: boolean;
	@Prop({ required: true }) lastActiveCollectionType: null | CollectionType;
	@Prop({ required: true }) locale: string;

	@Constant.Getter genresByShortName: Record<string, Genre>;
	@Constant.Getter providersByShortName: Record<string, Provider>;

	@Filter.Getter currentFilters: FilterCollection;
	@Filter.Getter filters: (collectionType: CollectionType) => FilterCollection;

	@Language.Getter country: string;
	@Language.State detectedWebLocale: WebLocale;
	@Language.State webLocale: WebLocale;

	@Routing.Getter urlMetadata: UrlMetadataResponse<HtmlContentMovieDetails>;
	@Routing.State activeRoute: any;
	@Routing.State lastRoute: Route;
	@Routing.State urlMetadataPromise: any;

	@SearchSuggester.Action setShown: (value: boolean) => void;

	@User.State onboarding_status: OnboardingStatus;
	@User.Getter isLoggedIn: () => boolean;
	@User.Getter isPremium: boolean;

	get isWatchlist(): boolean {
		const filters = this.filters(CollectionType.WATCHLIST);
		const routeName = StateHelper.getRouteName(this.webLocale, CollectionType.WATCHLIST, filters);
		return this.activeRoute?.name === routeName;
	}
	get isWatchlistTabActive() {
		const currentRoute = this.$route.name || '';
		return ['app.watchlist.list', 'app.shareable-lists'].some(route => currentRoute.startsWith(route));
	}

	get userLoggedIn(): boolean {
		return this.isLoggedIn();
	}

	get isHome(): boolean {
		return this.$route.name === 'app.home';
	}

	get isGlobal(): boolean {
		return this.onboarding_status === OnboardingStatus.GLOBAL;
	}

	get isUnsupportedCountry(): boolean {
		return this.onboarding_status === OnboardingStatus.UNSUPPORTED_COUNTRY;
	}

	get logoSrc() {
		const file = `JustWatch-logo-${this.isGlobal ? 'large' : 'small'}`;
		return {
			png: `${ASSETS_DIR}/img/logo/${file}.png`,
			webp: `${ASSETS_DIR}/img/logo/${file}.webp`,
		};
	}

	get hasSports() {
		return isSportSupported(this.webLocale);
	}

	get isValentine() {
		return new Date().getDate() === 14 && new Date().getMonth() === 1;
	}

	get isGuideLocale() {
		return isEditorialLocale(this.webLocale);
	}

	/**
	 * Inject routeParams from App.vue
	 */
	@Inject() routeParams: any;

	@Emit('tabChange')
	async onTabChange(newTab: string) {
		await this.urlMetadataPromise;
		const pageTypeContext = await TrackingHelper.getPageTypeContext(this.lastRoute.path, true);
		this.trackEvent('tabchange', newTab, [pageTypeContext]);

		providerPageExpTrackEvent(
			{
				label: 'tabchange',
				action: 'click',
			},
			this.lastRoute?.name as string
		);
		return newTab;
	}

	@Watch('activeRoute', { immediate: true })
	onActiveRoute(currentRoute: Location) {
		if (currentRoute.name && ['app.watchlist.list', 'app.shareable-lists'].includes(currentRoute.name)) {
			this.currentWatchlistRoute = currentRoute;
		}
	}

	trackEvent(action: string, label?: string, contexts: SnowplowContext[] = []) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action,
				label,
			},
			contexts
		);
		if (label === 'home') {
			const onboardingStatus =
				this.onboarding_status === OnboardingStatus.TASTE_ONBOARDING
					? OnboardingStatus.GLOBAL
					: this.onboarding_status;

			TrackingHelper.trackEvent('home_page_view', {
				action: onboardingStatus,
				property: this.detectedWebLocale || this.webLocale,
			});
		}

		if (action === 'click_logo') {
			providerPageExpTrackEvent(
				{
					label: 'click_logo',
					action: 'click',
				},
				this.lastRoute?.name as string
			);
		}
	}

	onOverlayClick() {
		this.setShown(false);
	}
}
